import * as React from 'react';

/** The bare query, for `matchMedia`. See `useReducedMotion` below. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Media query for viewers who asked the OS to minimize motion. Spread
 * `{[REDUCED_MOTION]: {transition: 'none'}}` into a style block to disable its
 * transitions for those users, or `{[REDUCED_MOTION]: {animationName: 'none'}}`
 * to stop a keyframe animation.
 *
 * Keep exactly one space after `@media`: styletron derives the media string by
 * slicing the key at that offset.
 */
export const REDUCED_MOTION = `@media ${REDUCED_MOTION_QUERY}`;

const subscribe = (onChange: () => void) => {
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener('change', onChange);
  return () => {
    query.removeEventListener('change', onChange);
  };
};

/**
 * `REDUCED_MOTION` for styles that can't go through `css()` — inline `style`
 * props, canvas, `scrollIntoView`, and the like. Prefer the media query when
 * the style does go through `css()`; this costs a re-render, that doesn't.
 */
export const useReducedMotion = (): boolean =>
  React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false,
  );
